import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface PetType {
  name: string;
  lifespan: string;
  trait: string;
  icon: string;
  color: string;
}

interface BehaviorSignal {
  name: string;
  meaning: string;
  animal: string;
  icon: string;
  color: string;
}

interface HealthItem {
  name: string;
  frequency: string;
  importance: string;
  color: string;
}

interface PetFact {
  label: string;
  icon: string;
}

interface NutritionLevel {
  name: string;
  percent: number;
  color: string;
  examples: string;
}

interface PetCareTemplateProps {
  topic: string;
  subtitle: string;
  petTypes: PetType[];
  behaviors: BehaviorSignal[];
  healthChecklist: HealthItem[];
  petFacts: PetFact[];
  nutritionPyramid: NutritionLevel[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: PetCareTemplateProps = {
  topic: '宠物饲养与行为科学',
  subtitle: 'Pet Care & Animal Behavior',
  petTypes: [
    {
      name: '犬',
      lifespan: '10-15年',
      trait: '忠诚社交，需要大量运动和陪伴',
      icon: '🐕',
      color: '#fb923c',
    },
    {
      name: '猫',
      lifespan: '12-18年',
      trait: '独立优雅，自我清洁能力强',
      icon: '🐈',
      color: '#f97316',
    },
    {
      name: '兔',
      lifespan: '8-12年',
      trait: '温顺安静，需要足够活动空间',
      icon: '🐇',
      color: '#fdba74',
    },
    {
      name: '仓鼠',
      lifespan: '2-3年',
      trait: '夜行性强，喜欢囤积食物',
      icon: '🐹',
      color: '#fed7aa',
    },
  ],
  behaviors: [
    {
      name: '摇尾巴',
      meaning: '兴奋/友好（犬）或烦躁（猫）',
      animal: '犬/猫',
      icon: '🐾',
      color: '#fb923c',
    },
    {
      name: '竖耳朵',
      meaning: '警觉/好奇，关注周围环境',
      animal: '犬/猫/兔',
      icon: '👂',
      color: '#f97316',
    },
    {
      name: '露肚皮',
      meaning: '极度信任（犬）或防御姿态（猫）',
      animal: '犬/猫',
      icon: '💛',
      color: '#fdba74',
    },
    {
      name: '磨蹭身体',
      meaning: '标记领地/表达亲近',
      animal: '猫',
      icon: '😻',
      color: '#c2410c',
    },
  ],
  healthChecklist: [
    { name: '疫苗接种', frequency: '幼年3针+每年加强', importance: '核心', color: '#f87171' },
    { name: '体内外驱虫', frequency: '每月/每三月', importance: '必要', color: '#fb923c' },
    { name: '绝育手术', frequency: '6月龄后一次', importance: '推荐', color: '#fbbf24' },
    { name: '定期体检', frequency: '每年1-2次', importance: '重要', color: '#34d399' },
  ],
  petFacts: [
    { label: '微表情识别', icon: '🔍' },
    { label: '正向强化', icon: '🎁' },
    { label: '环境丰容', icon: '🏠' },
    { label: '社会化训练', icon: '🤝' },
  ],
  nutritionPyramid: [
    { name: '优质蛋白', percent: 40, color: '#c2410c', examples: '鸡肉/鱼肉/蛋类' },
    { name: '脂肪酸', percent: 25, color: '#f97316', examples: '鱼油/亚麻籽' },
    { name: '碳水化合物', percent: 20, color: '#fb923c', examples: '糙米/红薯/燕麦' },
    { name: '维生素矿物', percent: 10, color: '#fdba74', examples: '蔬菜/水果/补剂' },
    { name: '水分', percent: 5, color: '#fed7aa', examples: '每日新鲜饮水' },
  ],
  stats: [
    { value: '5亿+', label: '全球家养宠物犬数量' },
    { value: '6亿+', label: '全球家养宠物猫数量' },
    { value: '15年+', label: '负责任养宠的承诺' },
  ],
  bottomQuote:
    '养宠物不只是一种爱好，而是一份长达十余年的生命契约——它们用全部的生命来爱你，请以同样的认真回报它们',
};

export const PetCareTemplate: React.FC<
  Partial<PetCareTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const petStart = 30;
  const behaviorStart = 160;
  const pyramidStart = 200;
  const healthStart = 280;
  const factStart = 370;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const pyramidDraw = interpolate(
    frame,
    [pyramidStart, pyramidStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #2a1508 0%, #120a04 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating paw print particles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 307) % 1920;
        const y = (i * 173) % 1080;
        const size = 4 + (i % 5);
        const drift = Math.sin(frame * 0.02 + i) * 8;
        const sway = Math.cos(frame * 0.018 + i * 0.6) * 5;
        const pulse = 0.05 + 0.04 * Math.sin(frame * 0.04 + i * 0.8);
        const colors = ['#fb923c', '#fdba74', '#f97316', '#c2410c'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + sway,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 2}px ${colors[i % colors.length]}30`,
            }}
          >
            {/* Tiny paw shape via small dots */}
            {i % 4 === 0 && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    top: -size * 0.5,
                    left: -size * 0.3,
                    width: size * 0.4,
                    height: size * 0.4,
                    borderRadius: '50%',
                    background: colors[i % colors.length],
                    opacity: 0.8,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: -size * 0.5,
                    left: size * 0.5,
                    width: size * 0.4,
                    height: size * 0.4,
                    borderRadius: '50%',
                    background: colors[i % colors.length],
                    opacity: 0.8,
                  }}
                />
              </>
            )}
          </div>
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#c2410c',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#ffedd5',
            flexShrink: 0,
          }}
        >
          🐾 宠物科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#ffedd5',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#9a7a62',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Pet Types ===== */}
      <div style={{ position: 'absolute', top: 120, left: 36, width: 400 }}>
        <div
          style={{
            fontSize: 26,
            color: '#9a7a62',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          常见宠物档案
        </div>
        <svg width="400" height="520" viewBox="0 0 400 520">
          <defs>
            <linearGradient id="petBgGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fb923c" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#c2410c" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          <rect
            x="10"
            y="10"
            width="380"
            height="500"
            rx="12"
            fill="url(#petBgGrad)"
            strokeWidth="2"
            stroke="#c2410c40"
            opacity={interpolate(frame, [petStart, petStart + 40], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          {p.petTypes.map((pet, i) => {
            const yPos = 30 + i * 120;
            const s = spring({
              frame: frame - (petStart + 20 + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <g key={i} opacity={s}>
                <circle
                  cx="55"
                  cy={yPos + 40}
                  r="30"
                  fill={`${pet.color}20`}
                  stroke={`${pet.color}40`}
                  strokeWidth="1.5"
                />
                <text
                  x="55"
                  y={yPos + 50}
                  fill={pet.color}
                  fontSize="32"
                  textAnchor="middle"
                >
                  {pet.icon}
                </text>

                <text
                  x="100"
                  y={yPos + 14}
                  fill="#ffedd5"
                  fontSize="22"
                  fontWeight="800"
                >
                  {pet.name}
                </text>
                <text
                  x="100"
                  y={yPos + 44}
                  fill={pet.color}
                  fontSize="20"
                  fontWeight="700"
                >
                  寿命 {pet.lifespan}
                </text>
                <text x="100" y={yPos + 74} fill="#9a7a62" fontSize="20">
                  {pet.trait}
                </text>

                {i < p.petTypes.length - 1 && (
                  <line
                    x1="40"
                    y1={yPos + 90}
                    x2="370"
                    y2={yPos + 90}
                    stroke={pet.color}
                    strokeWidth="1"
                    opacity="0.15"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== CENTER: Behavior Signals + Nutrition Pyramid ===== */}
      <div style={{ position: 'absolute', top: 120, left: 480, width: 400 }}>
        <div
          style={{
            fontSize: 26,
            color: '#9a7a62',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          宠物行为信号解读
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.behaviors.map((bh, i) => {
            const s = spring({
              frame: frame - (behaviorStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#2a150890',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${bh.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${bh.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${bh.color}30`,
                  }}
                >
                  {bh.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: bh.color }}
                    >
                      {bh.name}
                    </span>
                    <span style={{ fontSize: 20, color: '#9a7a62' }}>
                      {bh.animal}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 20, color: '#9a7a62', marginTop: 2 }}
                  >
                    {bh.meaning}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* ===== Nutrition Pyramid (right of behavior signals) ===== */}
      <div style={{ position: 'absolute', top: 120, left: 900, width: 560 }}>
        <div
          style={{
            fontSize: 26,
            color: '#9a7a62',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          宠物营养金字塔
        </div>
        <svg width="560" height="390" viewBox="0 0 560 390">
          {p.nutritionPyramid.map((level, i) => {
            const layerH = 74;
            const yTop = 10 + i * layerH;
            const topW = 180 + i * 75;
            const leftX = 260 - topW / 2;
            const levelS = spring({
              frame: frame - (pyramidStart + 20 + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });

            return (
              <g key={i} opacity={levelS * pyramidDraw}>
                <rect
                  x={leftX}
                  y={yTop}
                  width={topW}
                  height={layerH - 6}
                  rx="6"
                  fill={level.color}
                  opacity="0.5"
                />
                <text
                  x={leftX + topW / 2}
                  y={yTop + 27}
                  fill="#ffedd5"
                  fontSize="18"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {level.name} {level.percent}%
                </text>
                <text
                  x={leftX + topW / 2}
                  y={yTop + 53}
                  fill="#ffedd5"
                  fontSize="18"
                  textAnchor="middle"
                  opacity="0.85"
                >
                  {level.examples}
                </text>

                <line
                  x1={leftX + topW + 5}
                  y1={yTop + layerH / 2}
                  x2={510}
                  y2={yTop + layerH / 2}
                  stroke={level.color}
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  opacity="0.3"
                />
                <text
                  x={520}
                  y={yTop + layerH / 2 + 6}
                  fill={level.color}
                  fontSize="20"
                  fontWeight="700"
                >
                  {level.percent}%
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== RIGHT COLUMN: Health Checklist + Pet Facts ===== */}
      <div style={{ position: 'absolute', top: 120, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#9a7a62',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          健康管理清单
        </div>
        {p.healthChecklist.map((h, i) => {
          const s = spring({
            frame: frame - (healthStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#2a150890',
                borderRadius: 12,
                padding: '12px 16px',
                border: `1.5px solid ${h.color}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    padding: '2px 8px',
                    background: `${h.color}30`,
                    borderRadius: 4,
                    fontSize: 20,
                    fontWeight: 700,
                    color: h.color,
                  }}
                >
                  {h.importance}
                </div>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#ffedd5' }}>
                  {h.name}
                </span>
              </div>
              <div style={{ fontSize: 22, color: '#9a7a62', marginTop: 4 }}>
                频率: {h.frequency}
              </div>
            </div>
          );
        })}

        {/* Pet Facts */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#9a7a62',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            行为科学要素
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.petFacts.map((fact, i) => {
              const s = spring({
                frame: frame - (factStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#2a150890',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #c2410c40',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 4 }}>{fact.icon}</div>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: '#fb923c' }}
                  >
                    {fact.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#2a150890',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #c2410c40',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 34, fontWeight: 900, color: '#fb923c' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 20, color: '#9a7a62', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#2a150890',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #c2410c',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🐾</span>
          <span style={{ fontSize: 22, color: '#ffedd5', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #c2410c, #fb923c, #ffedd5)',
        }}
      />
    </AbsoluteFill>
  );
};
